import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { type Event } from 'nostr-tools';
import {
  TrendingUp, Users, Coins, FileText, Film, Music, CalendarDays,
  Heart, MessageCircle, Repeat2, Eye, Target, Star, Lightbulb,
  UserPlus, Wallet, Award, ArrowUpRight, Crown,
} from 'lucide-react';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useBCHPrice, getBCHPrice } from '../hooks/useBCHPrice';
import { useFollowers } from '../hooks/useFollows';
import {
  useCreatorMembership, useCreatorGoal, useGoalPledges, useSubscriptions,
} from '../pages/CreatorSupportPage';
import {
  goalProgress, satsToDisplay,
} from '../lib/creatorSupport';
import { timeAgo } from '../lib/utils';

// ── Kinds & Tags ────────────────────────────────────────────────────
const TIP_RECEIPT_KIND = 9735;
const VIDEO_KINDS = [22, 21, 34236, 34235];
const ARTICLE_KIND = 30023;
const RADIO_TRACK_KIND = 1808;
const RADIO_TRACK_TAG = 'bch-radio-track';
const DATE_EVENT_KIND = 31922;
const TIME_EVENT_KIND = 31923;
const BOOST_KIND = 30078;
const BOOST_TAG = 'bch-boost';

// ── Helper: extract sats from a tip event ───────────────────────────
function extractTipSats(ev: Event): number {
  let sats = 0;
  try {
    const amountTag = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
    if (amountTag) sats = parseInt(amountTag, 10) || 0;
    if (!sats) {
      const content = JSON.parse(ev.content || '{}');
      sats = content.amount_sats || 0;
    }
  } catch {}
  return sats;
}

// ── Stat Card ───────────────────────────────────────────────────────
function DashStat({ label, value, sub, icon: Icon, color }: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5 space-y-1">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <span className="text-[11px] text-muted-foreground font-medium leading-tight">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums leading-tight">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ── Section Header ──────────────────────────────────────────────────
function SectionHeader({ icon: Icon, label, color }: { icon: React.ElementType; label: string; color: string }) {
  return (
    <div className="flex items-center gap-2 pt-2 pb-1">
      <Icon className="w-4 h-4" style={{ color }} />
      <h3 className="text-sm font-bold">{label}</h3>
    </div>
  );
}

// ── Top Content Row ─────────────────────────────────────────────────
function TopContentRow({ rank, event, score, label }: {
  rank: number;
  event: Event;
  score: number;
  label: string;
}) {
  const noteId = event.id;
  const content = event.content?.slice(0, 80) || '(media)';
  const isArticle = event.kind === ARTICLE_KIND;
  const dTag = isArticle ? event.tags?.find(t => t[0] === 'd')?.[1] : null;

  return (
    <Link
      to={isArticle && dTag ? `/article/${event.pubkey}/${dTag}` : `/note/${noteId}`}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/50 transition-colors group"
    >
      <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
        rank === 1 ? 'bg-amber-500/15 text-amber-500' :
        rank === 2 ? 'bg-gray-400/15 text-gray-400' :
        rank === 3 ? 'bg-orange-500/15 text-orange-600' :
        'bg-muted text-muted-foreground'
      }`}>
        {rank}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs truncate group-hover:text-primary transition-colors">{content}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{label} · {timeAgo(event.created_at)}</p>
      </div>
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground shrink-0">
        <TrendingUp className="w-3 h-3" />
        {score}
      </div>
    </Link>
  );
}

// ── Insight Card ────────────────────────────────────────────────────
function InsightCard({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-primary/5 border border-primary/10">
      <Lightbulb className="w-4 h-4 text-primary shrink-0 mt-0.5" />
      <p className="text-xs text-foreground/80 leading-relaxed">{text}</p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ██  CREATOR DASHBOARD
// ══════════════════════════════════════════════════════════════════════

export default function CreatorDashboard({ pubkey }: { pubkey: string }) {
  const { price: bchPrice } = useBCHPrice();

  // ── Time boundaries ────────────────────────────────────────────────
  const nowSec = useMemo(() => Math.floor(Date.now() / 1000), []);
  const dayAgo = nowSec - 86400;
  const weekAgo = nowSec - 7 * 86400;
  const monthAgo = nowSec - 30 * 86400;
  // 6-month window for "all time" — beyond this, relay limits kick in
  const allTimeSince = nowSec - 180 * 86400;

  // ── Data queries ───────────────────────────────────────────────────
  // Tips received
  const { events: tipEvents, loading: tipsLoading } = useNostrQuery({
    filters: [{ kinds: [TIP_RECEIPT_KIND], '#p': [pubkey], '#t': ['bch-tip'], limit: 500, since: allTimeSince }],
    allRelays: true,
  });

  // Membership subscription receipts
  const { subs: membershipSubs, activeThisMonth, allTimeSupporters: allTimeMemberSupporters, loading: subsLoading } = useSubscriptions(pubkey);

  // Creator goal
  const goal = useCreatorGoal(pubkey);
  const { pledges: goalPledges, totalSats: goalRaisedSats } = useGoalPledges(
    goal?.event?.id, pubkey, goal?.data.createdAt,
  );

  // Followers
  const { followerCount, followers, loading: followersLoading } = useFollowers(pubkey);

  // Posts (kind 1)
  const { events: postEvents } = useNostrQuery({
    filters: [{ kinds: [1], authors: [pubkey], limit: 500, since: allTimeSince }],
    allRelays: true,
  });

  // Cash Clips (video kinds)
  const { events: clipEvents } = useNostrQuery({
    filters: [{ kinds: VIDEO_KINDS, authors: [pubkey], limit: 200, since: allTimeSince }],
    allRelays: true,
    extraRelays: ['wss://relay.divine.video'],
  });

  // Radio tracks
  const { events: radioEvents } = useNostrQuery({
    filters: [{ kinds: [RADIO_TRACK_KIND], authors: [pubkey], '#t': [RADIO_TRACK_TAG], limit: 200, since: allTimeSince }],
    allRelays: true,
  });

  // Articles (kind 30023)
  const { events: articleEvents } = useNostrQuery({
    filters: [{ kinds: [ARTICLE_KIND], authors: [pubkey], limit: 100, since: allTimeSince }],
    allRelays: true,
  });

  // Calendar events hosted
  const { events: calendarEvents } = useNostrQuery({
    filters: [{ kinds: [DATE_EVENT_KIND, TIME_EVENT_KIND], authors: [pubkey], limit: 100, since: allTimeSince }],
    allRelays: true,
  });

  // Engagement on creator's posts (reactions, replies, reposts)
  const postIds = useMemo(() => postEvents.slice(0, 200).map(e => e.id), [postEvents]);
  const { events: engagementEvents } = useNostrQuery({
    filters: postIds.length > 0 ? [
      { kinds: [7, 6, 1], '#e': postIds.slice(0, 150), limit: 1000, since: allTimeSince },
    ] : [],
    enabled: postIds.length > 0,
    allRelays: true,
  });

  // Boosts paid for this creator's content
  const { events: boostEvents } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [BOOST_TAG], '#p': [pubkey], limit: 100, since: allTimeSince }],
    allRelays: true,
  });

  // New followers this week — query kind 3 (contact list) events that mention this pubkey
  const { events: recentFollowEvents } = useNostrQuery({
    filters: [{ kinds: [3], '#p': [pubkey], limit: 200, since: weekAgo }],
    allRelays: true,
  });
  const newFollowersThisWeek = useMemo(() => {
    const pks = new Set<string>();
    for (const ev of recentFollowEvents) {
      if (ev.pubkey !== pubkey) pks.add(ev.pubkey);
    }
    return pks.size;
  }, [recentFollowEvents, pubkey]);

  // ══════════════════════════════════════════════════════════════════
  //  COMPUTED STATS
  // ══════════════════════════════════════════════════════════════════

  // ── Earnings ─────────────────────────────────────────────────────
  const tipStats = useMemo(() => {
    let todaySats = 0, weekSats = 0, monthSats = 0, allTimeSats = 0;
    let tipCount = 0;

    for (const ev of tipEvents) {
      const sats = extractTipSats(ev);
      if (sats <= 0) continue;
      tipCount++;
      allTimeSats += sats;
      if (ev.created_at >= monthAgo) monthSats += sats;
      if (ev.created_at >= weekAgo) weekSats += sats;
      if (ev.created_at >= dayAgo) todaySats += sats;
    }

    return { todaySats, weekSats, monthSats, allTimeSats, tipCount };
  }, [tipEvents, dayAgo, weekAgo, monthAgo]);

  const membershipRevenueSats = useMemo(() => {
    let total = 0;
    for (const ev of membershipSubs) {
      const amt = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
      if (amt) total += parseInt(amt, 10) || 0;
    }
    return total;
  }, [membershipSubs]);

  const boostRevenueSats = useMemo(() => {
    let total = 0;
    for (const ev of boostEvents) {
      try {
        const data = JSON.parse(ev.content || '{}');
        total += data.bidSats || data.amountSats || 0;
      } catch {
        const amt = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
        if (amt) total += parseInt(amt, 10) || 0;
      }
    }
    return total;
  }, [boostEvents]);

  const totalEarningsSats = tipStats.allTimeSats + membershipRevenueSats + goalRaisedSats + boostRevenueSats;

  // Format sats to display
  const fmtSats = (sats: number): string => {
    if (sats === 0) return '—';
    if (bchPrice) {
      const usd = (sats / 1e8) * bchPrice;
      if (usd >= 1) return `$${usd.toFixed(2)}`;
      if (usd >= 0.01) return `$${usd.toFixed(2)}`;
    }
    if (sats >= 1_000_000) return `${(sats / 1_000_000).toFixed(1)}M sats`;
    if (sats >= 1_000) return `${(sats / 1_000).toFixed(1)}k sats`;
    return `${sats.toLocaleString()} sats`;
  };

  // ── Audience ─────────────────────────────────────────────────────
  const uniqueSupporters = useMemo(() => {
    const pks = new Set<string>();
    // Tippers
    for (const ev of tipEvents) { if (ev.pubkey !== pubkey) pks.add(ev.pubkey); }
    // Members
    for (const ev of membershipSubs) { if (ev.pubkey !== pubkey) pks.add(ev.pubkey); }
    // Goal pledgers
    for (const ev of goalPledges) { if (ev.pubkey !== pubkey) pks.add(ev.pubkey); }
    return pks.size;
  }, [tipEvents, membershipSubs, goalPledges, pubkey]);

  // ── Content counts ───────────────────────────────────────────────
  const topLevelPosts = useMemo(() =>
    postEvents.filter(e => !e.tags?.some((t: string[]) => t[0] === 'e' && t[3] !== 'mention')),
    [postEvents]
  );

  // ── Top Performing Content ───────────────────────────────────────
  const topContent = useMemo(() => {
    // Build engagement map: postId → { replies, reactions, reposts, tipSats }
    const engMap = new Map<string, { replies: number; reactions: number; reposts: number; tipSats: number }>();

    for (const ev of engagementEvents) {
      const targetId = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (!targetId) continue;
      const entry = engMap.get(targetId) || { replies: 0, reactions: 0, reposts: 0, tipSats: 0 };
      if (ev.kind === 7) entry.reactions++;
      else if (ev.kind === 6) entry.reposts++;
      else if (ev.kind === 1) entry.replies++;
      engMap.set(targetId, entry);
    }

    // Add tips
    for (const ev of tipEvents) {
      const targetId = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (!targetId) continue;
      const sats = extractTipSats(ev);
      const entry = engMap.get(targetId) || { replies: 0, reactions: 0, reposts: 0, tipSats: 0 };
      entry.tipSats += sats;
      engMap.set(targetId, entry);
    }

    // Score each post
    const scored: { event: Event; score: number; label: string }[] = [];
    const allContent = [...topLevelPosts, ...clipEvents, ...articleEvents];
    const seen = new Set<string>();

    for (const ev of allContent) {
      if (seen.has(ev.id)) continue;
      seen.add(ev.id);
      const eng = engMap.get(ev.id) || { replies: 0, reactions: 0, reposts: 0, tipSats: 0 };
      // Weighted score: tips (highest weight) > replies > reactions > reposts
      const score = (eng.tipSats > 0 ? 50 : 0)
        + eng.replies * 5
        + eng.reactions * 2
        + eng.reposts * 3;
      if (score === 0) continue;

      const kind = ev.kind;
      const label = VIDEO_KINDS.includes(kind) ? 'Cash Clip'
        : kind === ARTICLE_KIND ? 'Article'
        : kind === 1068 ? 'Poll'
        : 'Post';

      scored.push({ event: ev, score, label });
    }

    return scored.sort((a, b) => b.score - a.score).slice(0, 5);
  }, [topLevelPosts, clipEvents, articleEvents, engagementEvents, tipEvents]);

  // ── Creator Insights ─────────────────────────────────────────────
  const insights = useMemo(() => {
    const result: string[] = [];

    // Clip vs post engagement comparison
    if (clipEvents.length > 0 && topLevelPosts.length > 0) {
      const clipEngPerItem = clipEvents.length > 0
        ? engagementEvents.filter(e => clipEvents.some(c => e.tags?.some((t: string[]) => t[0] === 'e' && t[1] === c.id))).length / clipEvents.length
        : 0;
      const postEngPerItem = topLevelPosts.length > 0
        ? engagementEvents.filter(e => topLevelPosts.some(p => e.tags?.some((t: string[]) => t[0] === 'e' && t[1] === p.id))).length / topLevelPosts.length
        : 0;
      if (clipEngPerItem > postEngPerItem * 1.3 && clipEngPerItem > 0) {
        result.push('Your Cash Clips receive more engagement per item than your posts. Consider creating more video content.');
      } else if (postEngPerItem > clipEngPerItem * 1.3 && postEngPerItem > 0) {
        result.push('Your text posts get more engagement per item than your clips. Your audience loves your writing.');
      }
    }

    // Best posting day
    if (tipEvents.length >= 3) {
      const dayTotals = new Map<number, number>();
      for (const ev of tipEvents) {
        const sats = extractTipSats(ev);
        if (sats <= 0) continue;
        // Find the post that was tipped
        const postId = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
        const post = postId ? topLevelPosts.find(p => p.id === postId) : null;
        if (post) {
          const day = new Date(post.created_at * 1000).getDay();
          dayTotals.set(day, (dayTotals.get(day) || 0) + sats);
        }
      }
      if (dayTotals.size > 0) {
        const bestDay = [...dayTotals.entries()].sort((a, b) => b[1] - a[1])[0];
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        result.push(`Your most tipped content was published on ${dayNames[bestDay[0]]}s.`);
      }
    }

    // Membership growth
    if (activeThisMonth.length > 0) {
      const lastMonthDate = new Date();
      lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
      const lastMonthStr = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;
      const lastMonthSubs = membershipSubs.filter(ev =>
        (ev.tags.find((t: string[]) => t[0] === 'month')?.[1] || '') === lastMonthStr
      );
      if (activeThisMonth.length > lastMonthSubs.length) {
        result.push(`Membership growth increased this month — ${activeThisMonth.length} active vs ${lastMonthSubs.length} last month.`);
      } else if (activeThisMonth.length === lastMonthSubs.length && activeThisMonth.length > 0) {
        result.push(`You have ${activeThisMonth.length} active member${activeThisMonth.length > 1 ? 's' : ''} this month — steady retention.`);
      }
    }

    // Goal progress
    if (goal?.data?.active && bchPrice) {
      const pct = goalProgress(goalRaisedSats, goal.data.targetUsd, bchPrice);
      if (pct >= 80) {
        result.push(`Your Creator Goal is ${pct}% complete — you're almost there!`);
      } else if (pct >= 50) {
        result.push(`Your Creator Goal is ${pct}% complete. Keep the momentum going.`);
      } else if (pct > 0) {
        result.push(`Your Creator Goal is ${pct}% funded. Share it with your audience to boost contributions.`);
      }
    }

    // New followers insight
    if (newFollowersThisWeek > 5) {
      result.push(`You gained ${newFollowersThisWeek} new followers this week. Your reach is growing.`);
    }

    // Tip streak
    if (tipStats.weekSats > 0 && tipStats.monthSats > 0) {
      const weekPct = tipStats.monthSats > 0 ? (tipStats.weekSats / tipStats.monthSats) * 100 : 0;
      if (weekPct > 40) {
        result.push('Tip velocity is strong — this week accounts for a large share of your monthly earnings.');
      }
    }

    // No tips
    if (tipStats.allTimeSats === 0 && topLevelPosts.length > 5) {
      result.push('You haven\'t received any tips yet. Adding a BCH address to your profile can help supporters find you.');
    }

    // No content
    if (topLevelPosts.length === 0 && clipEvents.length === 0) {
      result.push('Start posting to build your audience. Consistent content is the #1 driver of growth on BCH Nostr.');
    }

    return result.slice(0, 4); // max 4 insights
  }, [clipEvents, topLevelPosts, engagementEvents, tipEvents, tipStats, activeThisMonth, membershipSubs, goal, goalRaisedSats, bchPrice, newFollowersThisWeek]);

  // ══════════════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════════════

  const goalPct = goal?.data?.active && bchPrice
    ? goalProgress(goalRaisedSats, goal.data.targetUsd, bchPrice)
    : null;

  return (
    <div className="px-4 py-4 space-y-5">
      {/* ── 📈 Earnings ── */}
      <section>
        <SectionHeader icon={Coins} label="Earnings" color="#10B981" />
        <div className="grid grid-cols-2 gap-2.5 mt-2">
          <DashStat label="Today" value={fmtSats(tipStats.todaySats)} icon={TrendingUp} color="#10B981" />
          <DashStat label="7 Days" value={fmtSats(tipStats.weekSats)} icon={TrendingUp} color="#10B981" />
          <DashStat label="30 Days" value={fmtSats(tipStats.monthSats)} icon={TrendingUp} color="#10B981" />
          <DashStat label="All Time" value={fmtSats(totalEarningsSats)} icon={Wallet} color="#10B981"
            sub={totalEarningsSats > 0 && bchPrice ? `${(totalEarningsSats / 1e8).toFixed(4)} BCH` : undefined}
          />
        </div>

        <div className="grid grid-cols-2 gap-2.5 mt-2.5">
          <DashStat label="Tips Received" value={tipStats.tipCount} icon={Heart} color="#EF4444"
            sub={tipStats.allTimeSats > 0 ? fmtSats(tipStats.allTimeSats) + ' total' : undefined}
          />
          <DashStat
            label="Active Members"
            value={activeThisMonth.length}
            icon={Star}
            color="#F59E0B"
            sub={activeThisMonth.length > 0 ? 'this month' : 'No data yet'}
          />
          <DashStat
            label="Membership Revenue"
            value={membershipRevenueSats > 0 ? fmtSats(membershipRevenueSats) : '—'}
            icon={Award}
            color="#8B5CF6"
            sub={membershipRevenueSats === 0 ? 'No data yet' : `${allTimeMemberSupporters.length} supporters all time`}
          />
          <DashStat
            label="Boost Revenue"
            value={boostRevenueSats > 0 ? fmtSats(boostRevenueSats) : '—'}
            icon={ArrowUpRight}
            color="#3B82F6"
            sub={boostRevenueSats === 0 ? 'No data yet' : undefined}
          />
        </div>

        {/* Creator Goal Progress */}
        {goal?.data?.active && (
          <div className="mt-2.5 rounded-xl border border-border bg-card p-3.5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                <span className="text-xs font-bold">{goal.data.title}</span>
              </div>
              <span className="text-xs font-bold text-primary">{goalPct}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${goalPct || 0}%` }}
              />
            </div>
            <div className="flex justify-between mt-1.5 text-[10px] text-muted-foreground">
              <span>{satsToDisplay(goalRaisedSats, bchPrice)} raised</span>
              <span>Goal: ${goal.data.targetUsd}</span>
            </div>
          </div>
        )}
        {!goal?.data?.active && (
          <div className="mt-2.5 rounded-xl border border-border bg-card p-3.5">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">No active Creator Goal</span>
            </div>
          </div>
        )}
      </section>

      {/* ── 👥 Audience ── */}
      <section>
        <SectionHeader icon={Users} label="Audience" color="#6366F1" />
        <div className="grid grid-cols-2 gap-2.5 mt-2">
          <DashStat
            label="Followers"
            value={followersLoading ? '…' : followerCount}
            icon={Users}
            color="#6366F1"
          />
          <DashStat
            label="New Followers (7d)"
            value={newFollowersThisWeek}
            icon={UserPlus}
            color="#6366F1"
            sub={newFollowersThisWeek === 0 ? 'No data yet' : undefined}
          />
          <DashStat
            label="Unique Supporters"
            value={uniqueSupporters}
            icon={Heart}
            color="#EC4899"
            sub={uniqueSupporters === 0 ? 'No data yet' : 'tipped, members, or pledged'}
          />
          <DashStat
            label="Profile Views"
            value="—"
            icon={Eye}
            color="#8B5CF6"
            sub="Coming soon"
          />
        </div>
      </section>

      {/* ── 📝 Content ── */}
      <section>
        <SectionHeader icon={FileText} label="Content" color="#3B82F6" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-2">
          <DashStat label="Posts" value={topLevelPosts.length} icon={FileText} color="#3B82F6" />
          <DashStat label="Cash Clips" value={clipEvents.length} icon={Film} color="#F59E0B"
            sub={clipEvents.length === 0 ? 'No data yet' : undefined}
          />
          <DashStat label="Radio Songs" value={radioEvents.length} icon={Music} color="#EF4444"
            sub={radioEvents.length === 0 ? 'No data yet' : undefined}
          />
          <DashStat label="BCH Ink Articles" value={articleEvents.length} icon={FileText} color="#10B981"
            sub={articleEvents.length === 0 ? 'No data yet' : undefined}
          />
          <DashStat label="Events Hosted" value={calendarEvents.length} icon={CalendarDays} color="#10B981"
            sub={calendarEvents.length === 0 ? 'No data yet' : undefined}
          />
        </div>
      </section>

      {/* ── 🔥 Top Performing Content ── */}
      <section>
        <SectionHeader icon={Crown} label="Top Performing Content" color="#F59E0B" />
        {topContent.length > 0 ? (
          <div className="mt-2 rounded-xl border border-border bg-card overflow-hidden divide-y divide-border/50">
            {topContent.map((item, i) => (
              <TopContentRow
                key={item.event.id}
                rank={i + 1}
                event={item.event}
                score={item.score}
                label={item.label}
              />
            ))}
          </div>
        ) : (
          <div className="mt-2 rounded-xl border border-border bg-card p-4 text-center">
            <p className="text-xs text-muted-foreground">No data yet — engagement data will appear as your posts receive tips, replies, and reactions.</p>
          </div>
        )}
      </section>

      {/* ── 💡 Creator Insights ── */}
      <section>
        <SectionHeader icon={Lightbulb} label="Creator Insights" color="#0AC18E" />
        <div className="mt-2 space-y-2">
          {insights.length > 0 ? (
            insights.map((text, i) => <InsightCard key={i} text={text} />)
          ) : (
            <div className="rounded-xl border border-border bg-card p-4 text-center">
              <p className="text-xs text-muted-foreground">No data yet — insights will appear as you create content and build your audience.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer note */}
      <div className="text-center pb-2">
        <p className="text-[10px] text-muted-foreground/40">
          Data sourced from Nostr relays. Numbers may vary based on relay availability.
        </p>
      </div>
    </div>
  );
}

